import { useCallback } from "react";
import { pickBy } from "lodash";
import { useHistory } from "react-router-dom";
import type { useLocation } from "react-router-dom";
import { posts } from "@circle-react/helpers/urlHelpers/posts";
import type { Post } from "@circle-react/types";

interface SerializedLocation {
  hash: string;
  pathname: string;
  search: string;
  state: unknown;
}

interface NavigateToPostOptions {
  hasNextPage?: boolean;
  isWithinProfileContext?: boolean;
  newPost?: Post;
  params?: Record<string, unknown> | string;
  pathname?: string;
  prevLocation?: ReturnType<typeof useLocation> | SerializedLocation;
  profileContextMember?: {
    id: number;
    public_uid: string;
  };
  shouldDisableNavigation?: boolean;
  shouldFocusOnCommentsSection?: boolean;
}

export const useNavigateToPost = () => {
  const history = useHistory();

  if (!history) {
    throw new Error("useNavigateToPost must be used within a Router component");
  }

  const navigateToPost = useCallback(
    ({
      newPost,
      pathname,
      prevLocation,
      params,
      hasNextPage,
      shouldFocusOnCommentsSection = false,
      shouldDisableNavigation = false,
      isWithinProfileContext,
      profileContextMember,
    }: NavigateToPostOptions): void => {
      const serializedPrevLocation = prevLocation
        ? {
            pathname: prevLocation.pathname,
            search: prevLocation.search,
            hash: prevLocation.hash,
            state: prevLocation.state,
          }
        : undefined;

      const state = pickBy(
        {
          prevLocation: serializedPrevLocation,
          params,
          hasNextPage,
          shouldFocusOnCommentsSection,
          shouldDisableNavigation,
          isWithinProfileContext,
          profileContextMember,
        },
        value => value !== undefined,
      );

      if (pathname) {
        history.push(pathname, state);
      } else {
        history.push({
          pathname: posts.show({
            slug: newPost?.slug,
            spaceSlug: newPost?.space_slug,
          }),
          state,
        });
      }
    },
    [history],
  );

  return { navigateToPost };
};
